import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { AlignLeft, ArrowLeft, Copy, Check, Upload, Lock } from "lucide-react";
import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SummarizerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.plan || "free";
  const wordLimit = plan === "free" ? 2000 : 6000;
  const [text, setText] = useState("");
  const [length, setLength] = useState("medium");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploadedText, setUploadedText] = useState(""); // Extracted from uploaded file
  const fileRef = useRef(null);
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  if (!user) {
    return null; // ProtectedRoute handles redirect
  }

  const handleSummarize = async () => {
    // Use uploaded extracted text if available, otherwise use pasted text
    const sourceText = uploadedText || text.trim();
    if (!sourceText) return;
    const sourceWordCount = sourceText.trim().split(/\s+/).length;
    if (!uploadedText && wordCount > wordLimit) { setResult({ error: true, message: `Input must be under ${wordLimit} words.${plan === "free" ? " Upgrade to Pro for up to 6,000 words." : ""}` }); return; }
    setLoading(true); setResult(null);
    const token = localStorage.getItem("buddy_access_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/summarize`, { text: sourceText, length }, { headers });
      // Preserve uploaded text reference when summarizing from file
      if (uploadedText) data.extracted_text = uploadedText;
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploadedText("");
    setText("");
    setLoading(true); setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("buddy_access_token");
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/summarize-upload`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      // Persist extracted text so user can switch lengths without re-upload
      if (data.extracted_text) setUploadedText(data.extracted_text);
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to process file.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const clearUploaded = () => {
    setUploadedText("");
    setFileName("");
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCopy = (txt, id) => { try { navigator.clipboard.writeText(txt); } catch {} setCopied(id); setTimeout(() => setCopied(null), 2000); };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-6 pb-16">
        <button onClick={() => navigate("/ai-tools")} className="flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--rolex-green)" }}><ArrowLeft size={14} /> AI Tools</button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#E8F2EC" }}><AlignLeft size={20} style={{ color: "#5B8A72" }} /></div>
          <div><h1 className="font-display text-2xl font-bold" style={{ color: "var(--text-primary)" }}>Summarizer</h1><p className="text-sm" style={{ color: "var(--text-muted)" }}>Paste text or upload PDF/DOCX. Free: {plan === "free" ? "5 lifetime" : "Unlimited"} | Max {wordLimit.toLocaleString()} words</p></div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Upload */}
            <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <input type="file" ref={fileRef} accept=".pdf,.docx,.txt" className="hidden" onChange={handleFileUpload} />
              {uploadedText ? (
                <div className="space-y-3" data-testid="uploaded-doc-card">
                  <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--rolex-green-light)" }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Upload size={16} style={{ color: "var(--rolex-green)" }} />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: "var(--rolex-green)" }} data-testid="uploaded-file-name">{fileName}</p>
                        <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{uploadedText.split(/\s+/).length.toLocaleString()} words extracted • Switch length below</p>
                      </div>
                    </div>
                    <button onClick={clearUploaded} className="text-xs px-2 py-1 rounded-md hover:opacity-70" style={{ color: "var(--text-muted)" }} data-testid="clear-uploaded-btn">Remove</button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="label-caps">Summary Length</span>
                    <div className="flex gap-1.5">
                      {[{id: "short", label: "Short", w: "~40w"}, {id: "medium", label: "Medium", w: "~80w"}, {id: "detailed", label: "Detailed", w: "~120w"}].map(l => (
                        <button key={l.id} onClick={() => setLength(l.id)} className="text-[10px] px-2 py-1 rounded-md font-medium" style={length === l.id ? { background: "var(--rolex-green)", color: "white" } : { background: "var(--surface-warm)", color: "var(--text-muted)" }} data-testid={`upload-length-${l.id}`}>{l.label} {l.w}</button>
                      ))}
                    </div>
                  </div>
                  <motion.button onClick={handleSummarize} disabled={loading} className="btn-primary w-full py-2.5 text-sm" style={{ background: "var(--rolex-green)" }} data-testid="resummarize-btn">
                    {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto block" /> : `Re-summarize as ${length.charAt(0).toUpperCase() + length.slice(1)}`}
                  </motion.button>
                </div>
              ) : (
                <motion.button onClick={() => fileRef.current?.click()} className="w-full py-5 rounded-xl border-2 border-dashed flex flex-col items-center gap-1.5 transition-colors hover:bg-white" style={{ borderColor: "var(--surface-border-mid)" }} whileHover={{ borderColor: "var(--rolex-green)" }} data-testid="upload-trigger">
                  <Upload size={22} style={{ color: "var(--rolex-green)" }} />
                  <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>Upload PDF, DOCX, or TXT</span>
                  {fileName && <span className="text-[10px]" style={{ color: "var(--rolex-green)" }}>{fileName}</span>}
                </motion.button>
              )}
            </div>
            {/* Paste */}
            <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <textarea className="buddy-input resize-none" style={{ minHeight: "160px" }} rows={6} placeholder="Or paste your text here..." value={text} onChange={(e) => setText(e.target.value)} data-testid="summarizer-input" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs" style={{ color: wordCount > wordLimit ? "var(--error)" : "var(--text-light)" }}>{wordCount} / {wordLimit.toLocaleString()} words</span>
                <div className="flex gap-1.5">
                  {[{id: "short", label: "Short", w: "~40w"}, {id: "medium", label: "Medium", w: "~80w"}, {id: "detailed", label: "Detailed", w: "~120w"}].map(l => (
                    <button key={l.id} onClick={() => setLength(l.id)} className="text-[10px] px-2 py-1 rounded-md font-medium" style={length === l.id ? { background: "var(--rolex-green)", color: "white" } : { background: "var(--surface-warm)", color: "var(--text-muted)" }} data-testid={`length-${l.id}`}>{l.label} {l.w}</button>
                  ))}
                </div>
              </div>
              <motion.button onClick={handleSummarize} disabled={!text.trim() || loading || wordCount > wordLimit} className="btn-primary w-full py-2.5 text-sm mt-3" style={{ opacity: !text.trim() || wordCount > wordLimit ? 0.5 : 1, background: "var(--rolex-green)" }} data-testid="summarizer-submit">
                {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto block" /> : "Summarize"}
              </motion.button>
            </div>
          </div>
          {/* Result */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "380px" }}>
            {loading ? <div className="space-y-3 mt-4"><div className="h-4 rounded animate-shimmer" style={{ width: "90%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "70%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "80%" }} /></div>
            : result && !result.error ? (
              <div className="space-y-4">
                {result.truncation_notice && <p className="text-xs p-2 rounded-lg" style={{ background: "var(--warning-bg)", color: "var(--warning)" }}>{result.truncation_notice}</p>}
                {result.tldr && (
                  <div className="p-3 rounded-xl" style={{ background: "var(--rolex-green-light)" }}>
                    <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-bold" style={{ color: "var(--rolex-green)" }}>KEY TAKEAWAY</span><button onClick={() => handleCopy(result.tldr, "tldr")} className="text-[10px]" style={{ color: copied === "tldr" ? "var(--success)" : "var(--rolex-green)" }}>{copied === "tldr" ? "Copied!" : "Copy"}</button></div>
                    <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{result.tldr}</p>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-2"><span className="label-caps">Summary</span><button onClick={() => handleCopy(result.summary, "sum")} className="text-xs flex items-center gap-1" style={{ color: copied === "sum" ? "var(--success)" : "var(--rolex-green)" }}>{copied === "sum" ? <Check size={12} /> : <Copy size={12} />}{copied === "sum" ? "Copied" : "Copy"}</button></div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{result.summary}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px]" style={{ color: "var(--text-light)" }}>{result.original_word_count} words → {result.word_count || result.summary?.split(/\s+/).length} words</span>
                    {result.uses_remaining !== "unlimited" && result.uses_remaining !== undefined && <span className="text-[10px]" style={{ color: "var(--rolex-green)" }}>{result.uses_remaining} free uses left</span>}
                  </div>
                </div>
                {result.key_points?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2"><span className="label-caps">Key Points</span><button onClick={() => handleCopy(result.key_points.join("\n"), "kp")} className="text-[10px]" style={{ color: copied === "kp" ? "var(--success)" : "var(--rolex-green)" }}>{copied === "kp" ? "Copied!" : "Copy"}</button></div>
                    <ul className="space-y-1.5">{result.key_points.map((p, i) => <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}><span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: "var(--rolex-green)" }} />{p}</li>)}</ul>
                  </div>
                )}
              </div>
            ) : result?.error ? <p className="text-sm p-4 rounded-xl" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{result.message}</p>
            : <div className="flex flex-col items-center justify-center py-16"><AlignLeft size={36} style={{ color: "var(--surface-border-mid)" }} /><p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Your summary will appear here</p></div>}
          </div>
        </div>
      </main>
    </div>
  );
}
